import { getNextColor } from "@fluentui/react-charting";
import { getAbbrevatedValue } from "../../../Utils/js-common";
import { HEALTH_VIEW_DATA } from "./HealthView.constants";
import { IconButton, Stack, TooltipHost } from "@fluentui/react";
import { CustomMetricsCard } from "../../../SharedComponents/CustomMetricsCard";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { HEALTH_VIEW_STYLES } from "./HealthView.styles";
import React from "react";

export const HealthViewMetricsCards = ( props: {
  totalE5SeatsField: string,
  totalCustomersField: string,
  totalTenantsField?: string,
  totalUATField: string,
  totalDCRField: string,
  totalCRMField: string,
  totalActiveSRField: string,
  totalCritSitField: string,
  data: any[]
}) => {
  const totalE5Seats = props.data.reduce((sum, item) => {
    const val = Number(item[props.totalE5SeatsField]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const totalCustomers = Array.from(new Set(props.data.map((item) => item[props.totalCustomersField]))).length;
  const totalTenants = props.totalTenantsField
    ? Array.from(new Set(props.data.map((item) => item[props.totalTenantsField!]))).length
    : 0;
  const totalUAT = props.data.reduce((sum, item) => {
    const val = Number(item[props.totalUATField]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const totalCRM = props.data.reduce((sum, item) => {
    const val = Number(item[props.totalCRMField]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const totalDCR = props.data.reduce((sum, item) => {
    const val = Number(item[props.totalDCRField]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const totalActiveSR = props.data.reduce((sum, item) => {
    const val = Number(item[props.totalActiveSRField]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const totalCritSit = props.data.reduce((sum, item) => {
    const val = Number(item[props.totalCritSitField]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  const metricdata = React.useMemo(
    () => [
      {
        header: "Customer Profile",
        description: HEALTH_VIEW_DATA.METRICS_CARDS_HEADER_DESCRIPTION.CustomerProfile,
        metrics: [
          {
            title: "Total E5 Seats",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalE5Seats,
            currentValue: totalE5Seats.toString(),
            abbrevatedValue: getAbbrevatedValue(totalE5Seats),
            color: getNextColor(0)
          },
          {
            title: "Total Customers",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalCustomers,
            currentValue: totalCustomers.toString(),
            abbrevatedValue: getAbbrevatedValue(totalCustomers),
            color: getNextColor(1)
          },
          ...(props.totalTenantsField ? [{
            title: "Total Tenants",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalTenants,
            currentValue: totalTenants.toString(),
            abbrevatedValue: getAbbrevatedValue(totalTenants),
            color: getNextColor(2)
          }] : [])
        ]
      },
      {
        header: "Feature Requests",
        description: HEALTH_VIEW_DATA.METRICS_CARDS_HEADER_DESCRIPTION.FeatureRequests,
        metrics: [
          {
            title: "UATs",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalUAT,
            currentValue: totalUAT.toString(),
            abbrevatedValue: getAbbrevatedValue(totalUAT),
            color: getNextColor(3)
          },
          {
            title: "DCRs",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalDCR,
            currentValue: totalDCR.toString(),
            abbrevatedValue: getAbbrevatedValue(totalDCR),
            color: getNextColor(4)
          },
          {
            title: "CRM FRs",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalCRM,
            currentValue: totalCRM.toString(),
            abbrevatedValue: getAbbrevatedValue(totalCRM),
            color: getNextColor(5)
          }
        ]
      },
      {
        header: "Support",
        description: HEALTH_VIEW_DATA.METRICS_CARDS_HEADER_DESCRIPTION.Support,
        metrics: [
          {
            title: "Active SRs",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalActiveSR,
            currentValue: totalActiveSR.toString(),
            abbrevatedValue: getAbbrevatedValue(totalActiveSR),
            color: getNextColor(6)
          },
          {
            title: "Critisit Cases",
            description: HEALTH_VIEW_DATA.METRICS_CARDS_DESCRIPTION.totalCritSit,
            currentValue: totalCritSit.toString(),
            abbrevatedValue: getAbbrevatedValue(totalCritSit),
            color: getNextColor(7)
          }
        ]
      }
    ],
    [totalE5Seats, totalCustomers, totalTenants, totalUAT, totalDCR, totalCRM, totalActiveSR, totalCritSit]
  );
  return (
    <>
    <FluentProvider theme={webLightTheme}>
      <div className={HEALTH_VIEW_STYLES.COMMON_STYLES.COUNT_ANNOTATION.cardContent}>
        <div className={HEALTH_VIEW_STYLES.COMMON_STYLES.COUNT_ANNOTATION.metricsContainer}>
          {/* Render each group with header and tooltip, and pass metrics to CountAnnotation */}
          {metricdata.map((group) => (
            <div key={group.header} style={{ marginBottom: 10, marginRight: 0, width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontWeight: "bold", fontSize: 15 }}>{group.header}</span>
                {/* TooltipHost and IconButton for tooltip, as in CountAnnotation */}
                <span>
                  <TooltipHost content={group.description}>
                    <IconButton
                      iconProps={{ iconName: "Info" }}
                      styles={{
                        root: { background: "transparent", color: "#888" },
                        rootHovered: { background: "transparent", color: "#333" }
                      }}
                      ariaLabel={`Info about ${group.header}`}
                    />
                  </TooltipHost>
                </span>
              </div>
              <Stack
                horizontal
                wrap
                tokens={{ childrenGap: 10 }}
                style={{ flexWrap: "wrap", rowGap: 10, width: "100%" }}
              >
                {group.metrics.map((metric: any, idx: number) => (
                  <div key={idx} style={{ marginTop: 10, marginBottom: 10, marginRight: 15 }}>
                    <CustomMetricsCard {...metric} showTooltipIcon={true} />
                  </div>
                ))}
              </Stack>
            </div>
          ))}
        </div>
      </div>
    </FluentProvider>
    </>
  );
};
